import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FormProvider, useForm, useFormState } from 'react-hook-form';
import RMSCommonDialog from '../../shared/components/dialog/RMSCommonDialog';
import { RMSConfirmationDialog } from '../../shared/components/dialog';
import { RMSMenuSidebar } from '../../shared/components/sidebars';
import RMSRecordHistoryButton from '../../shared/components/modules/RMSRecordHistoryButton';
import useConfirmDialog from '../../shared/utility/useConfirmDialog';
import CreateFormResolver from '../../shared/utility/createFormResolver';
import { executeApiAction, executeMultipleApiActions } from '../../shared/utility/apiHelper';
import usePermissions from '../../shared/hooks/usePermissions';
import { PERMISSION_RESOURCES } from '../../shared/constants/permissionResources';
import { notify } from 'reducers/NotifierReducer';
import { getZone, updateZone, getZoneAgencies } from '../services/zoneService';
import { getZoneSops } from '../services/sopService';
import zoneSchema from '../schema/zoneSchema';
import { ZONE_DEFAULT_VALUES } from '../constants/zoneFormConstants';
import {
  ZONE_MENU_KEYS,
  ZONE_MENU_ITEMS,
  ZONE_SIDEBAR_GROUPS,
} from '../constants/zoneSidebarMenuHelper';
import {
  formatCodeZones,
  normalizeZoneAgencyId,
  getZoneAgencyOptions,
} from '../utility/zoneFormHelper';
import { hideEditZoneDialog, editZoneDialogSaved } from '../store/actions/editZoneDialog.actions';

const EditZoneDialog = ({ dialog }) => {
  const dispatch = useDispatch();
  const permissions = usePermissions(PERMISSION_RESOURCES.ZONE);

  const [record, setRecord] = useState(null);
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [activeKey, setActiveKey] = useState(ZONE_MENU_KEYS.DETAILS);
  const [sopCount, setSopCount] = useState(0);

  const methods = useForm({
    defaultValues: ZONE_DEFAULT_VALUES,
    resolver: CreateFormResolver(zoneSchema),
    mode: 'onBlur',
    disabled: !permissions.Edit || loading || submitting,
  });
  const { reset } = methods;
  const { isDirty } = useFormState({ control: methods.control });
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();

  const close = () => dispatch(hideEditZoneDialog(dialog.requestId));

  const leave = (action) => {
    if (submitting) return;
    if (activeKey === ZONE_MENU_KEYS.DETAILS && isDirty)
      requestConfirm(
        'You have unsaved zone changes. Leave without saving?',
        () => {
          reset({ ...ZONE_DEFAULT_VALUES, ...record });
          action();
        },
        'Unsaved Changes'
      );
    else action();
  };

  const save = async (values) => {
    if (!record || !permissions.Edit || submitting) return;
    await executeApiAction({
      dispatch,
      setLoading: setSubmitting,
      action: () => updateZone(record.ZoneCode, formatCodeZones(values)),
      onSuccess: (saved) => {
        const updated = {
          ...record,
          ...values,
          ...saved,
          AgencyID: normalizeZoneAgencyId(saved?.AgencyID ?? values.AgencyID),
        };
        setRecord(updated);
        reset(updated);
        dispatch(notify('Zone updated successfully', 'success'));
        dispatch(editZoneDialogSaved(updated, dialog.requestId));
      },
    });
  };

  const onSopsChanged = useCallback((count) => setSopCount(count), []);

  const groups = ZONE_SIDEBAR_GROUPS.map((group) => ({
    ...group,
    items: group.items.map((item) =>
      item.key === ZONE_MENU_KEYS.SOPS
        ? { ...item, label: sopCount ? 'SOPs (' + sopCount + ')' : 'SOPs' }
        : item
    ),
  }));

  const ActiveContent = ZONE_MENU_ITEMS.find((item) => item.key === activeKey)?.content;

  useEffect(() => {
    let active = true;
    const loadZone = async () => {
      let loadedZone = null;
      let agencyRows = [];
      let sopCount = 0;
      await executeMultipleApiActions({
        dispatch,
        setLoading: (value) => {
          if (active) setLoading(value);
        },
        actions: [
          {
            action: async () => {
              const zone = await getZone(dialog.zoneCode);
              if (!zone) throw new Error('Zone not found.');
              const sops = await getZoneSops(zone.IntCode);
              return { zone, count: (sops?.data || sops || []).length };
            },
            onSuccess: ({ zone, count }) => {
              loadedZone = zone;
              sopCount = count;
            },
          },
          {
            action: getZoneAgencies,
            onSuccess: (rows) => {
              agencyRows = rows;
            },
          },
        ],
      });
      if (!active || !loadedZone) return;
      const normalizedZone = {
        ...loadedZone,
        AgencyID: normalizeZoneAgencyId(loadedZone.AgencyID),
      };
      setRecord(normalizedZone);
      reset({ ...ZONE_DEFAULT_VALUES, ...normalizedZone });
      setSopCount(sopCount);
      setAgencies(getZoneAgencyOptions(agencyRows, loadedZone.AgencyID));
    };
    loadZone();
    return () => {
      active = false;
    };
  }, [dialog.zoneCode, dispatch, reset]);

  return (
    <RMSCommonDialog
      open
      fullScreen
      title={permissions.Edit ? 'Edit Zone' : 'View Zone'}
      className="edit-zone-page-content"
      onClose={() => leave(close)}
      onSave={methods.handleSubmit(save)}
      loading={loading || submitting}
      saveDisabled={!record || !permissions.Edit || loading || submitting}
      saveButton={{ show: activeKey === ZONE_MENU_KEYS.DETAILS, type: 'simple', label: 'Save' }}
      leftActions={
        record &&
        activeKey === ZONE_MENU_KEYS.DETAILS && (
          <RMSRecordHistoryButton
            disabled={!record.Created}
            created={record.Created}
            createdBy={record.CreatedBy}
            updated={record.Updated}
            updatedBy={record.UpdatedBy}
          />
        )
      }>
      <div className="edit-zone-page">
        <RMSMenuSidebar
          groups={groups}
          activeKey={activeKey}
          onSelect={(key) => {
            if (key !== activeKey) leave(() => setActiveKey(key));
          }}
          className="app-sidebar--bordered"
        />
        <main
          className={
            'edit-zone-page__content' +
            (activeKey === ZONE_MENU_KEYS.DETAILS ? '' : ' edit-zone-page__content--grid')
          }>
          {record && ActiveContent && (
            <FormProvider {...methods}>
              <ActiveContent
                agencies={agencies}
                record={record}
                permissions={permissions}
                onSopsChanged={onSopsChanged}
              />
            </FormProvider>
          )}
        </main>
      </div>
      {confirmState.open && (
        <RMSConfirmationDialog
          open
          title={confirmState.title}
          message={confirmState.message}
          onYes={handleYes}
          onNo={handleNo}
        />
      )}
    </RMSCommonDialog>
  );
};
export default EditZoneDialog;
